import { useCallback, useEffect, useState } from 'react';
import {
  getEffectiveAllowlist,
  getEnvironmentRoutes,
  listBotIpNames,
  listFriendAllowlistAudit,
  updateBotIpName,
  updateFriendAllowlist,
} from '../../api/adminApi.js';
import type {
  AllowlistAction,
  AllowlistAuditEntry,
  EffectiveAllowlistConversation,
  EnvironmentRouteAccount,
} from '../../api/types.js';
import { botAccountDisplayName, displayName } from '../../lib/utils.js';

interface EnvironmentSettingsProps {
  open: boolean;
  onClose: () => void;
  onSaved: (message: string, type?: 'success' | 'error') => void;
}

export function EnvironmentSettings({ open, onClose, onSaved }: EnvironmentSettingsProps) {
  const [routeRows, setRouteRows] = useState<EnvironmentRouteAccount[]>([]);
  const [allowlistRows, setAllowlistRows] = useState<EffectiveAllowlistConversation[]>([]);
  const [knownIpNames, setKnownIpNames] = useState<string[]>([]);
  const [ipDrafts, setIpDrafts] = useState<Record<number, string>>({});
  const [savingIpId, setSavingIpId] = useState<number | null>(null);
  const [redisKey, setRedisKey] = useState('');
  const [operationReason, setOperationReason] = useState('');
  const [activeAuditFriendId, setActiveAuditFriendId] = useState<number | null>(null);
  const [audit, setAudit] = useState<AllowlistAuditEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingFriendId, setSavingFriendId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [routes, allowlist, ipNames] = await Promise.all([
        getEnvironmentRoutes(),
        getEffectiveAllowlist(),
        listBotIpNames(),
      ]);
      setRouteRows(routes.accounts);
      setAllowlistRows(allowlist.conversations);
      setKnownIpNames(ipNames.knownIpNames);
      setIpDrafts(Object.fromEntries(
        routes.accounts.map((account) => [account.wechatRecordId, account.ipName ?? '']),
      ));
      setRedisKey(allowlist.redisKey);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void load();
  }, [load, open]);

  const saveIp = useCallback(async (row: EnvironmentRouteAccount) => {
    const ipName = ipDrafts[row.wechatRecordId] ?? '';
    setSavingIpId(row.wechatRecordId);
    try {
      const result = await updateBotIpName(row.wechatRecordId, ipName);
      setRouteRows((current) => current.map((candidate) => (
        candidate.wechatRecordId === row.wechatRecordId
          ? { ...candidate, ipName: result.account.ipName }
          : candidate
      )));
      setIpDrafts((current) => ({ ...current, [row.wechatRecordId]: result.account.ipName ?? '' }));
      onSaved('当前 IP 已保存', 'success');
    } catch (err) {
      onSaved(err instanceof Error ? err.message : '保存失败', 'error');
    } finally {
      setSavingIpId(null);
    }
  }, [ipDrafts, onSaved]);

  const operateAllowlist = useCallback(async (row: EffectiveAllowlistConversation, action: AllowlistAction) => {
    setSavingFriendId(row.friendId);
    try {
      const result = await updateFriendAllowlist(row.friendId, {
        action,
        environment: row.environment === 'prod' ? 'prod' : 'dev',
        reason: operationReason || undefined,
        operator: 'admin',
      });
      setAllowlistRows((current) => current.map((candidate) => (
        candidate.friendId === row.friendId
          ? { ...candidate, isWhitelisted: result.decision.allowed, allowlist: result.decision }
          : candidate
      )));
      setOperationReason('');
      onSaved(actionLabel(action), 'success');
      if (activeAuditFriendId === row.friendId) {
        const nextAudit = await listFriendAllowlistAudit(row.friendId);
        setAudit(nextAudit.audit);
      }
    } catch (err) {
      onSaved(err instanceof Error ? err.message : '操作失败', 'error');
    } finally {
      setSavingFriendId(null);
    }
  }, [activeAuditFriendId, onSaved, operationReason]);

  const showAudit = useCallback(async (friendId: number) => {
    setActiveAuditFriendId(friendId);
    try {
      const result = await listFriendAllowlistAudit(friendId);
      setAudit(result.audit);
    } catch (err) {
      onSaved(err instanceof Error ? err.message : '审计加载失败', 'error');
    }
  }, [onSaved]);

  if (!open) return null;

  return (
    <div className="settings-overlay" role="presentation">
      <button className="settings-backdrop" type="button" aria-label="关闭设置" onClick={onClose} />
      <section className="settings-panel" role="dialog" aria-modal="true" aria-labelledby="environment-settings-title">
        <header className="settings-header">
          <div>
            <h2 id="environment-settings-title">白名单与环境</h2>
            <div className="settings-subtitle">
              {routeRows.length} 个账号 · {allowlistRows.length} 个会话
            </div>
          </div>
          <div className="settings-actions">
            <button className="button" type="button" onClick={() => void load()} disabled={loading}>刷新</button>
            <button className="button ghost" type="button" onClick={onClose}>关闭</button>
          </div>
        </header>

        <div className="settings-body">
          {error ? <div className="settings-error">{error}</div> : null}
          {loading ? <div className="empty-state small">加载中</div> : (
            <>
              <section className="settings-section">
                <h3>工作微信环境</h3>
                <div className="settings-table route-table">
                  <div className="settings-row header">
                    <span>账号</span>
                    <span>微信 ID</span>
                    <span>当前 IP</span>
                    <span />
                    <span>Bridge</span>
                    <span>Router</span>
                    <span>状态</span>
                  </div>
                  {routeRows.map((row) => {
                    const ipDraft = ipDrafts[row.wechatRecordId] ?? '';
                    const ipDirty = ipDraft.trim() !== (row.ipName ?? '');
                    return (
                      <div className="settings-row" key={row.wechatRecordId}>
                        <AccountCell name={row.nickname} id={row.wechatRecordId} />
                        <div className="settings-wechat">{row.wechatId || '-'}</div>
                        <input
                          className="settings-input"
                          list="known-ip-names"
                          maxLength={20}
                          value={ipDraft}
                          onChange={(event) => setIpDrafts((current) => ({
                            ...current,
                            [row.wechatRecordId]: event.target.value,
                          }))}
                          placeholder="输入 IP 名"
                        />
                        <div className="allowlist-actions">
                          <button
                            className="button"
                            type="button"
                            disabled={!ipDirty || savingIpId === row.wechatRecordId}
                            onClick={() => void saveIp(row)}
                          >
                            {savingIpId === row.wechatRecordId ? '保存中' : '存 IP'}
                          </button>
                        </div>
                        <span className="tag">{row.bridgeEnvironment}</span>
                        <span className="tag">{row.routerEnvironment}</span>
                        <span className={row.risks.length ? 'route-risk bad' : 'route-risk'}>
                          {row.risks.length ? row.risks.join(', ') : '正常'}
                        </span>
                      </div>
                    );
                  })}
                  {routeRows.length === 0 ? <div className="empty-state small">暂无工作微信账号</div> : null}
                </div>
              </section>

              <section className="settings-section">
                <h3>客户 friendId 白名单</h3>
                <div className="settings-subtitle">Redis Key: {redisKey || '-'}</div>
                <label className="allowlist-reason">
                  <span>操作原因</span>
                  <input className="settings-input" value={operationReason} onChange={(event) => setOperationReason(event.target.value)} placeholder="填写本次变更原因" />
                </label>
                <div className="settings-table allowlist-table">
                  <div className="settings-row header">
                    <span>客户</span>
                    <span>工作微信</span>
                    <span>环境</span>
                    <span>来源</span>
                    <span>状态</span>
                    <span />
                  </div>
                  {allowlistRows.map((row) => (
                    <div className="settings-row" key={row.friendId}>
                      <div className="settings-account">
                        <strong>{displayName(row)}</strong>
                        <span>friendId #{row.friendId}</span>
                      </div>
                      <span>{botAccountDisplayName(row)}</span>
                      <span className="tag">{row.environment}</span>
                      <span>{sourceText(row)}</span>
                      <span className={row.allowlist.allowed ? 'tag whitelist' : 'tag'}>{row.allowlist.allowed ? '允许' : '拦截'}</span>
                      <div className="allowlist-actions">
                        <button className="button" type="button" disabled={savingFriendId === row.friendId} onClick={() => void operateAllowlist(row, 'allow')}>允许</button>
                        <button className="button ghost" type="button" disabled={savingFriendId === row.friendId} onClick={() => void operateAllowlist(row, 'deny')}>禁用</button>
                        <button className="button ghost" type="button" disabled={savingFriendId === row.friendId} onClick={() => void operateAllowlist(row, 'remove')}>移除</button>
                        <button className="button ghost" type="button" onClick={() => void showAudit(row.friendId)}>审计</button>
                      </div>
                    </div>
                  ))}
                  {allowlistRows.length === 0 ? <div className="empty-state small">暂无会话</div> : null}
                </div>
              </section>

              {activeAuditFriendId ? (
                <section className="settings-section">
                  <h3>friendId #{activeAuditFriendId} 审计</h3>
                  <div className="audit-list">
                    {audit.map((entry) => (
                      <div className="audit-row" key={entry.id}>
                        <strong>{entry.action}</strong>
                        <span>{entry.previousValue ?? 'missing'} -&gt; {entry.nextValue ?? 'missing'}</span>
                        <span>{entry.operator}</span>
                        <span>{entry.reason || '-'}</span>
                        <span>{entry.createdAt || '-'}</span>
                      </div>
                    ))}
                    {audit.length === 0 ? <div className="empty-state small">暂无审计记录</div> : null}
                  </div>
                </section>
              ) : null}
            </>
          )}
          <datalist id="known-ip-names">
            {knownIpNames.map((name) => <option key={name} value={name} />)}
          </datalist>
        </div>
      </section>
    </div>
  );
}

function AccountCell({ name, id }: { name: string | null; id: number }) {
  return (
    <div className="settings-account">
      <strong>{name || `账号 ${id}`}</strong>
      <span>#{id}</span>
    </div>
  );
}

function actionLabel(action: AllowlistAction): string {
  if (action === 'allow') return '已加入白名单';
  if (action === 'deny') return '已禁用白名单';
  return '已移除动态覆盖';
}

function sourceText(row: EffectiveAllowlistConversation): string {
  if (row.allowlist.sources.length > 0) return row.allowlist.sources.join(' + ');
  return row.allowlist.reason;
}
